<template>
  <FolderCard
    v-if="props.viewType === DataViewType.Grid" :folder="item" :view-type="FolderViewType.MyVideosView" @delete-folder="deleteFolder"
    @click="openViewFolder"
  />
  <div
    v-if="props.viewType === DataViewType.List"
    ref="dataViewRow"
    class="folder list"
    :style="`grid-template-columns:  ${columnsGridTemplate(props.columns)};`"
    @click="openViewFolder"
  >
    <div class="head" :class="shouldHideOnBreakpoint(props.columns, 'name')">
      <div class="title">
        <NewTooltip :placement="'top'" :content="props.item.name" auto-ellipsis />
      </div>
    </div>
    <div class="head total-videos" :class="shouldHideOnBreakpoint(props.columns, 'number_of_videos')">
      <div class="title videos-number">
        {{ props.item.number_of_videos }}
      </div>
      <div class="small-list-videos">
        <PlaylistImage
          v-for="index in indicesToShow"
          :key="index"
          :source="getThumbnail(index)"
          :blank="props.item.videos && props.item.videos.length <= index"
          class="thumbnails"
          :border-radius="[3, 3, 3, 3]"
        />
        <PlaylistImage :source="getThumbnail(4)" :blank="props.item.videos && props.item.videos.length < 5" class="thumbnails" :border-radius="[3, 3, 3, 3]">
          <div v-if="props.item.number_of_videos > 5" class="morevideos">+{{ props.item.number_of_videos - numberOfVideosToShow - 1 }}</div>
        </PlaylistImage>
      </div>
    </div>

    <div class="head" :class="shouldHideOnBreakpoint(props.columns, 'create_date')">
      <div class="title">
        {{ getDateString(props.item.create_date) }}
      </div>
    </div>
    <ActionButtons :actions="actions" :class="shouldHideOnBreakpoint(props.columns, 'actions')" :is-visible="isRowHovered" @click.stop />
  </div>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router'
import PlaylistImage from '@/components/content/playlist/PlaylistImage.vue'
import { getDateString, openModalCustomConfirmation, isDefaultGeneralFolder } from '@/services/utils'
import { NewTooltip } from '@/components/common'
import { CRUD, DataType, DataViewType, DataActions } from '@/interfaces'
import ActionButtons from '@/components/common/action-buttons/ActionButtons.vue'
import { columnsGridTemplate, shouldHideOnBreakpoint } from '@/services/layoutUtils'
import { ref, computed, onMounted, onBeforeUnmount, inject } from 'vue'
import { useElementHover } from '@vueuse/core'
import addVideosIconPlaceholder from '@/assets/playlists/playlist/add-videos-icon-placeholder.svg'
import { ModalType } from '@/components/modals/modals-util'
import { apiService } from '@/services'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { VIDEO_ACTIONS_INJECTION_KEY } from '@/constants'
import FolderCard from '@/components/content/videos/folders/folder/FolderCard.vue'
import { checkPermission } from '@/store/permissions'
import { useFoldersPlaylist } from '@/components/content/videos/folders/useFoldersPlaylist'
import { FolderViewType } from '@/components/content/videos/folders/types'

interface FolderDeleteParams {
  id: number
}

const store = useStore()
const { t } = useI18n()
const dataActions = inject<DataActions>(VIDEO_ACTIONS_INJECTION_KEY)
const image = new window.Image()
image.src = addVideosIconPlaceholder

const props = defineProps(['item', 'viewType', 'columns', 'detailsLink', 'pageRoute'])
const router = useRouter()
const dataViewRow = ref()
const isRowHovered = useElementHover(dataViewRow)
const { getDisabledTooltip, createAddFolderToPlaylist } = useFoldersPlaylist()
const disabledTooltip = getDisabledTooltip(props.item)
const addFolderToPlaylist = createAddFolderToPlaylist(props.item, store.state.user.user.organization_id)
const actions = computed(() => [
  ...(checkPermission('playlists')
    ? [
        {
          label: 'add',
          handler: addFolderToPlaylist,
          translationKey: 'add-to-playlist',
          isDisabledWithTooltip: disabledTooltip.value
        }
      ]
    : []),
  {
    label: 'edit',
    handler: openEditFolder,
    translationKey: 'edit',
    isDisabled: isDefaultGeneralFolder(props.item.name)
  },
  {
    label: 'delete',
    handler: openDeleteFolder,
    translationKey: 'delete',
    isDisabled: isDefaultGeneralFolder(props.item.name)
  }
])
const modalType = ModalType.FOLDERS_DELETE_FOLDER_MODAL

function openViewFolder() {
  router.push({
    name: 'view-folder',
    params: { folderId: props.item.id }
  })
}

function openEditFolder() {
  router.push({
    name: 'edit-folder',
    params: { id: props.item.id }
  })
}

function openDeleteFolder() {
  openModalCustomConfirmation({ action: CRUD.Delete, type: DataType.Folder, itemName: props.item.title, id: props.item.id, onConfirm: deleteFolder }, modalType)
}

const deleteFolder = async (params: FolderDeleteParams) => {
  return apiService.folder
    .deleteFolder(params.id)
    .then(() => {
      store.dispatch('messages/addMessage', {
        message: t('folder-delete-msg'),
        type: 'success'
      })
      if (dataActions?.refreshData) dataActions.refreshData()
    })
    .catch((err) => {
      store.dispatch('messages/addMessage', {
        message: err.response.data.error.description,
        type: 'fail'
      })
    })
}

function getThumbnail(i: number): string {
  const video = props?.item?.videos?.[i]
  if (!video || !Array.isArray(video.thumbnails) || video.thumbnails.length === 0) {
    return image.src
  }
  const index = video.is_vertical && video.thumbnails[3]?.url ? 3 : 0
  return video.thumbnails[index]?.url || image.src
}

const windowWidth = ref(window.innerWidth)
const numberOfVideosToShow = computed(() => {
  if (windowWidth.value < 1550) {
    return 2 // Show 2 videos for small screens
  } else if (windowWidth.value < 1700) {
    return 3 // Show 3 videos for medium screens
  } else if (windowWidth.value < 2000) {
    return 4 // Show 4 videos for medium screens
  } else {
    return 5 // Show 5 videos for large screens
  }
})
const indicesToShow = computed(() => {
  return Array.from({ length: numberOfVideosToShow.value }, (_, i) => i)
})

function handleResize() {
  windowWidth.value = window.innerWidth
}

onMounted(() => {
  window.addEventListener('resize', handleResize)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
})
</script>

<style scoped lang="scss">
.folder.list {
  width: 100%;
  height: fit-content;
  cursor: pointer;

  .total-videos {
    display: flex;

    .videos-number {
      width: 9.625rem;
    }
  }

  .small-videos {
    cursor: pointer;
    display: flex;
    flex-flow: row;
    justify-content: space-between;
    margin: 8px 0 7px 0;
    box-sizing: border-box;

    .thumbnails {
      width: 23.6%;
      padding-bottom: 16%;
      border-radius: 4px;

      .morevideos {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
        background-color: #ffffff34;
        font-size: 18px;
        font-weight: 700;
        line-height: 25px;
        text-transform: capitalize;
        color: #ffffff;
      }
    }
  }

  .head {
    overflow: hidden;
    padding: 1rem;
  }

  &.list {
    width: 100%;
    display: grid;
    align-items: center;

    &:hover {
      background-color: #f2f4fa;
    }

    .icon {
      cursor: pointer;
    }

    .title {
      display: grid;
      grid-template-columns: 100%;
      justify-content: left;
      align-items: center;
      font-weight: 400;
      font-size: 16px;
      line-height: 22px;
      letter-spacing: 0.1px;
      color: #4c4c66;

      .title-text {
        color: var(--body-text, #4c4c66);
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 16px;
        font-weight: 600;
      }
    }

    .columns-text {
      overflow: hidden;
      color: var(--body-text, #4c4c66);
      text-overflow: ellipsis;
      font-size: 14px;
      font-weight: 600;
      letter-spacing: 0.1px;
    }

    .videos-amount-number {
      display: flex;
      width: 22px;
      height: 22px;
      justify-content: center;
      align-items: center;
      gap: 10px;
      border-radius: 30px;
      background: #dadbe8;
      border: 1px solid $inputBorder;
      color: #4c4c66;
      text-align: center;
      font-size: 12px;
      font-weight: 600;
      margin-right: 8px;
    }

    .videos-amount {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      font-size: 16px;
      font-weight: 600;
      line-height: 22px;
      letter-spacing: 0.10000000149011612px;
      text-align: left;
      color: #4c4c66;
    }

    .flex-one {
      flex: 1;
    }

    .date {
      font-size: 16px;
      font-weight: 600;
      line-height: 22px;
      letter-spacing: 0.10000000149011612px;
      text-align: left;
      color: #4c4c66;
    }

    .tags {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      font-size: 12px;
      font-weight: 600;
      line-height: 16px;
      letter-spacing: 0px;
      text-align: center;
      color: #4c4c66;
      background: none;
      border-radius: 0;
      margin-right: 20px;
      padding: 0;
      gap: 8px;

      .videos-amount-number {
        display: flex;
        width: 27px;
        height: 26px;
        justify-content: center;
        align-items: center;
        gap: 10px;
        border-radius: 30px;
        background: var(--title-2, #7d81b2);
        border: 1px solid #7d81b2;
        color: var(--white, #fff);
        text-align: center;
        font-size: 12px;
        font-weight: 600;
      }

      .tag {
        padding: 3px 20px;
        background-color: #f2f4fa;
        border-radius: 4px;
        margin: 0;
        display: flex;
        max-width: fit-content;
      }
    }
  }
}

.menu-settings {
  margin-top: 30px;
  padding-right: 25px;
  width: 100%;
  position: absolute;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  cursor: pointer;

  img {
    position: absolute;
  }
}

.content {
  display: flex;
  flex-direction: column;

  &:hover {
    .additionalFolders {
      background-color: #7d81b2;
      border: 1px solid #7d81b2;
    }
  }

  .small-list-videos {
    cursor: pointer;
    display: flex;
    flex-flow: row;
    justify-content: space-between;
    box-sizing: border-box;
    gap: 1rem;

    .thumbnails {
      height: 3.3rem;
      width: 3.3rem;
      border-radius: 3px;
      padding: 0;

      .morevideos {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
        background-color: #ffffff34;
        font-size: 18px;
        font-weight: 700;
        line-height: 25px;
        text-transform: capitalize;
        color: #ffffff;
      }
    }
  }

  .folder-info {
    display: flex;
    flex-direction: column;

    .name {
      color: var(--white, #fff);
      font-size: 18px;
      font-weight: 700;
    }

    .description {
      color: var(--white, #fff);
      font-size: 14px;
      font-weight: 600;
      letter-spacing: 0.1px;
    }

    .date {
      color: var(--white, #fff);
      font-size: 12px;
      font-style: normal;
      font-weight: 300;
      line-height: normal;
    }
  }
}

.thumbnails {
  width: 100%;
  padding-bottom: 60%;
  border-radius: 6px 6px 0px 0px;
  background: #f2f4fa;

  img.image {
    border-radius: 6px 6px 0 0;
  }

  &:hover {
    .view {
      opacity: 1;
    }
  }

  .info {
    top: 8px;
    position: absolute;
    display: flex;
    justify-content: space-between;
    width: 100%;
    padding: 0 20px;

    .title {
      font-style: normal;
      font-weight: 700;
      font-size: 18px;
      line-height: 25px;
      color: #ffffff;
      margin-bottom: 2px;
    }

    .params {
      font-style: normal;
      font-weight: 300;
      font-size: 12px;
      line-height: 16px;
      letter-spacing: 0.1px;
      text-transform: capitalize;
      color: #ffffff;
      display: flex;
      justify-content: left;
      align-items: center;

      .dot {
        display: flex;
        width: 19px;
        justify-content: center;
        align-items: center;
      }
    }
  }

  &.list {
    width: 80px;
    padding-bottom: 0;
    height: 53px;
    border-radius: 6px;
    margin-right: 32px;
    cursor: pointer;
  }
}

.row {
  display: flex;
}

.controls {
  display: flex;
  align-items: center;

  img {
    margin-left: 20px;
    cursor: pointer;
  }
}

.flex-between {
  justify-content: space-between;
  align-items: flex-start;
  position: relative;
}

.title {
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 19px;
  color: #7d81b3;
  margin: 16px 0;
}

.categories {
  display: flex;
  align-items: center;
  margin: 0 16px 0 16px;

  .item {
    background: #dadbe8;
    border: 1px solid $inputBorder;
    border-radius: 2px;
    margin-right: 4px;
    padding: 3px 13px;
  }

  .additional {
    font-style: normal;
    font-weight: 600;
    font-size: 12px;
    line-height: 16px;
    text-align: center;
    text-transform: capitalize;
    color: #4c4c66;
    margin-left: 10.5px;
  }
}

.folders {
  display: flex;
  align-items: center;
  padding: 16px;
  border-radius: 0px 0px 6px 6px;
  background: var(--filed, #f2f4fa);

  .item {
    border-radius: 2px;
    margin-right: 8px;
    padding: 3px 13px;
    border: 1px solid var(--title-3, #dadbe8);
    background: var(--title-3, #dadbe8);
    color: var(--body-text, #4c4c66);
    text-align: center;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.1px;
  }

  .additionalFolders {
    display: flex;
    width: 27px;
    height: 26px;
    justify-content: center;
    align-items: center;
    gap: 10px;
    border-radius: 30px;
    background: var(--title-2, #7d81b2);
    border: 1px solid #7d81b2;
    color: var(--white, #fff);
    text-align: center;
    font-size: 12px;
    font-weight: 600;
  }
}

.listFolders {
  display: flex;
  align-items: center;

  .item {
    margin-right: 8px;
    padding: 3px 13px;
    border-radius: 4px;
    border: 1px solid var(--title-3, #dadbe8);
    background: var(--title-3, #dadbe8);
    color: #4c4c66;
    text-align: center;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.1px;
  }

  .additionalFolders {
    display: flex;
    width: 27px;
    height: 26px;
    justify-content: center;
    align-items: center;
    gap: 10px;
    border-radius: 30px;
    background: var(--title-2, #7d81b2);
    color: var(--white, #fff);
    text-align: center;
    font-size: 12px;
    font-weight: 600;
  }
}
</style>
